/* eslint-disable guard-for-in */
import { type Dispatch } from 'redux';
import { type EmptyAction, createAction } from 'typesafe-actions';

import { AppActions } from '../constants/App';
import { getIsBodyScrollingEnabled, getIsScrollingEnabled } from '../lib/UIStateUtils';

interface SetScrollPosPayload {
  offset: number;
}

/**
 * Action creator triggered by scrolling window position vertically
 *
 * @param  {Number} offset - The y-axis position of the window from the top
 */
export const setScroll = createAction(AppActions.SET_SCROLL_POS, (offset: number) => ({
  offset,
}))<SetScrollPosPayload>();
export type SetScrollPosAction = ReturnType<typeof setScroll>;

const scrollListeners: Record<string, EventListener> = {
  // these events should be sufficient for the platforms we support but can
  // easily be added to if necessary
  wheel: e => {
    e.stopPropagation();
    e.preventDefault();
  },
  scroll: e => {
    e.stopPropagation();
    e.preventDefault();
  },
};

// exporting for testing purposes
export const enableScrollListeners = (): void => {
  for (const name in scrollListeners) window.addEventListener(name, scrollListeners[name], true);
  document.body.style.overflowY = 'hidden';
};

// exporting for testing purposes
export const disableScrollListeners = (): void => {
  for (const name in scrollListeners) window.removeEventListener(name, scrollListeners[name], true);
  // use the default
  document.body.style.overflowY = '';
};

export type DisableScrollingAction = EmptyAction<typeof AppActions.DISABLE_SCROLLING>;
/**
 * Disable all scrolling for the application. Disabling scrolling implies the
 * caller will ultimately be responsible to enable scrolling when the time
 * is appropriate. This action will do nothing if scrolling is not enabled.
 *
 * @returns {Function} The action-thunk
 */
export const disableScrolling =
  () =>
  (dispatch: Dispatch<DisableScrollingAction>, getState: $TSFixMeFunction): void => {
    if (getIsScrollingEnabled(getState())) {
      dispatch({ type: AppActions.DISABLE_SCROLLING });
      enableScrollListeners();
    }
  };

export type EnableScrollingAction = EmptyAction<typeof AppActions.ENABLE_SCROLLING>;
/**
 * Enable scrolling for the application. This does nothing if scrolling is not
 * disabled.
 *
 * @returns {Function} The action-thunk
 */
export const enableScrolling =
  () =>
  (dispatch: Dispatch<EnableScrollingAction>, getState: $TSFixMeFunction): void => {
    if (!getIsScrollingEnabled(getState())) {
      dispatch({ type: AppActions.ENABLE_SCROLLING });
      disableScrollListeners();
    }
  };

export type DisableBodyScrollingAction = EmptyAction<typeof AppActions.DISABLE_BODY_SCROLLING>;
/**
 * Disable scrolling just for the body of the application. Disabling scrolling
 * implies the caller will ultimately be responsible to enable scrolling when the time
 * is appropriate. This action will do nothing if body scrolling is not enabled.
 * @param  {Boolean} isFixedPosition - Apply fixed position for specific overlays
 * @returns {Function} The action-thunk
 */
export const disableBodyScrolling =
  (isFixedPosition = false) =>
  (dispatch: Dispatch<DisableBodyScrollingAction>, getState: $TSFixMeFunction): void => {
    if (getIsBodyScrollingEnabled(getState())) {
      dispatch({ type: AppActions.DISABLE_BODY_SCROLLING });
      document.body.style.overflow = 'hidden';
      if (isFixedPosition) {
        document.body.style.position = 'fixed';
      }
    }
  };

export type EnableBodyScrollingAction = EmptyAction<typeof AppActions.ENABLE_BODY_SCROLLING>;
/**
 * Enable scrolling for the body of the application. This does nothing if scrolling is
 * not disabled.
 *
 * @returns {Function} The action-thunk
 */
export const enableBodyScrolling =
  () =>
  (dispatch: Dispatch<EnableBodyScrollingAction>, getState: $TSFixMeFunction): void => {
    if (!getIsBodyScrollingEnabled(getState())) {
      dispatch({ type: AppActions.ENABLE_BODY_SCROLLING });
      document.body.style.overflow = '';
      document.body.style.position = '';
    }
  };
